/**
 * The wire shape for one Ask Vera tool call's structured result, plus pure client-side
 * logic over it (VRG-227).
 *
 * This file IS the contract. The server half (apps/api/src/features/ask/*, built
 * concurrently on a separate branch) is generated from this file's literal type text, so
 * both sides cannot independently invent their own shape — which is exactly what
 * happened once already today: the server emitted `{name, arguments}` while the client
 * read `{name, input, status}`, both sides declaring their own object type, and every
 * tool call silently stuck on "running" with no compiler error anywhere to catch it.
 *
 * Two problems this exists to fix:
 *
 * 1. Only a summary STRING reaches the client today (`AskToolCall.summary` in
 *    `ask/types.ts`) — the model's narration of the rows, not the rows themselves. A
 *    wrong summary is indistinguishable from a right one. Shipping the actual rows
 *    makes a narration error visible.
 * 2. An answer is a dead end. "Omega Speedmaster, 412 scans" should be one click from
 *    the certificates that made up that number.
 */

/**
 * The record a dimension value names, when it names one at all. `country`, `month`,
 * `day` etc. are not records — a row grouped by one of those has no `entity`.
 */
export interface AskResultEntity {
  kind: 'product' | 'owner' | 'certificate'
  id: string
}

/** One row of a tool result. Keys are dimension names + one aggregate column. */
export interface AskResultRow {
  /** Display label for the dimension value, e.g. "Omega Speedmaster", "France". */
  label: string
  /** The aggregate for this row (count / sum / avg), already computed server-side. */
  value: number
  /**
   * The entity this row IS, when the dimension names a first-class record. Absent for
   * dimensions that are not records (country, city, month, day). This is what makes
   * both the preview render and the deep link possible — a display label alone can
   * neither be looked up nor put in a URL filter.
   */
  entity?: AskResultEntity
}

/**
 * The metric vocabulary, mirrored by hand from the server's `AskMetricName`
 * (apps/api/src/features/ask/ask.tools.ts) — the two apps have no shared type package for
 * this domain, so this is the one place on the CLIENT the union lives; `toolLabels.ts` and
 * anything else client-side that needs it imports it from here rather than re-typing the
 * five names a third time. `ask-tool-coverage.guard.spec.ts` (apps/api/tests/unit/)
 * cross-checks this against the server's live `ASK_METRIC_NAMES` at test time, so a metric
 * added to one side and not the other fails a guard instead of drifting silently — see
 * that spec's docblock.
 */
export type AskMetricName =
  | 'scans'
  | 'certificates'
  | 'owners'
  | 'leads'
  | 'transfers'
  /**
   * VRG-567 — the three all-time money figures from the Analytics hero. They share the
   * `metric` vocabulary but not its machinery: the server routes them to
   * `GetRoiMetricsUseCase` rather than to the SQL grammar, and they carry NO dimension.
   *
   * ── THE DEEP-LINK DECISION, made rather than dodged (working-conventions §63) ────────
   * These three have no destination, and the reason is not "we didn't get to it": there
   * is no link SURFACE to put one on. `AskResultView` renders its container — and with it
   * the only place a deep link appears — solely when `rows.length > 1` or `unattributed`
   * is set. A money metric is one row with no unattributed count, by construction (one
   * figure, one payer, nothing to rank), so `AskResultView` returns `null` and
   * `buildAskDeepLink` is never reached for them. Wiring `/analytics` into
   * `buildRankingDeepLink` would be unreachable code that reads like a shipped feature.
   *
   * The coverage guard does not force the question either — it derives its pairs from
   * `ALLOWED_METRIC_DIMENSIONS`, and these metrics contribute none — so this note is the
   * record that the decision was taken, which is what §63 actually asks for. If a future
   * change gives the panel a single-row surface, that is when the link becomes reachable
   * and worth adding.
   */
  | 'return_on_vera'
  | 'value_delivered'
  | 'vera_spend'

export interface AskResultSet {
  metric: AskMetricName
  dimension?: string
  aggregate: 'count' | 'sum' | 'avg'
  /**
   * Ranking direction of `rows`, as the query produced them. "Top N" style aggregate
   * queries are DESC by convention (highest value first) — that convention is what a
   * deep link to a sortable destination (e.g. the owners list, which reads `?sort=` +
   * `?order=` straight off the URL — see `buildAskDeepLink`'s ranking branch) has to
   * reproduce, rather than landing on the destination's own default sort and silently
   * showing the right rows in the wrong order. Defaults to `'desc'` when omitted.
   */
  order?: 'asc' | 'desc'
  rows: AskResultRow[]
  /** Rows the query could not attribute (NULL dimension), excluded from ranking. */
  unattributed?: number
  /** Echoed filters the query actually ran with, for the deep link + the caption. */
  filters?: Record<string, string | undefined>
}

export interface AskDeepLink {
  to: string
  label: string
}

const encode = (id: string): string => encodeURIComponent(id)

/**
 * `?filters=<field>:isAnyOf:<id>` — NOT invented here. This is the existing convention
 * every list page's own `parseReferenceFilter` helper reads:
 *   - `apps/platform/src/pages/main/CertificatesPage/CertificatesPage.tsx:125-169`
 *     (reads both `product` and `owner`)
 *   - `apps/platform/src/pages/main/ProductsPage/ProductsPage.tsx:15-21,122`
 *     (reads `owner`)
 *   - `apps/platform/src/pages/main/PeoplePage/PeoplePage.tsx:56-70,131`
 *     (reads `product`)
 * and the shared `Table` component parses the same param shape for its own filter model
 * (`Table.tsx:761-770`, splitting on `&` then `:`).
 */
const referenceFilter = (field: 'product' | 'owner', id: string): string =>
  `filters=${field}:isAnyOf:${encode(id)}`

/**
 * Drill into ONE entity's own records — the row-scoped case ("Omega Speedmaster, 412
 * scans" → this product's certificates). Every branch below was verified by reading the
 * destination page's own param-parsing code; a combination not listed was checked and
 * found to have no destination (see the accompanying report for the full list).
 */
const buildRowDeepLink = (result: AskResultSet, entity: AskResultEntity): AskDeepLink | null => {
  const { metric } = result

  switch (entity.kind) {
    case 'certificate':
      // An id-scoped record route always resolves regardless of metric — no filter, sort
      // or tab needed. Pick the sub-route the metric is actually about (both confirmed
      // registered in App.tsx and rendered from the certificate's own `:id` — CertificatePage.tsx).
      if (metric === 'scans') {
        return { to: `/certificates/${encode(entity.id)}/scans`, label: 'View scan history' }
      }
      if (metric === 'transfers') {
        return { to: `/certificates/${encode(entity.id)}/activity`, label: 'View activity' }
      }
      return { to: `/certificates/${encode(entity.id)}`, label: 'View certificate' }

    case 'product':
      if (metric === 'certificates') {
        return { to: `/certificates?${referenceFilter('product', entity.id)}`, label: 'Show these certificates' }
      }
      if (metric === 'owners') {
        return { to: `/people?${referenceFilter('product', entity.id)}`, label: 'Show these customers' }
      }
      // scans / transfers / leads dimensioned by product: verified NO destination exists —
      // ProductPage.tsx has no scans, transfers or leads tab or filter param at all.
      return null

    case 'owner':
      if (metric === 'certificates') {
        return { to: `/certificates?${referenceFilter('owner', entity.id)}`, label: 'Show these certificates' }
      }
      if (metric === 'leads') {
        // `?tab=leads&referrer=<ownerId>` — verified in
        // PeoplePage/components/LeadsTab/LeadsTab.tsx:65-71 (the exact mechanism the Owners
        // tab's own "Leads" count cell already links through).
        return { to: `/people?tab=leads&referrer=${encode(entity.id)}`, label: 'Show these leads' }
      }
      if (metric === 'owners') {
        // Degenerate as a ranking (owners metric grouped by owner), but a safe, always-valid
        // fallback: the owner's own detail page.
        return { to: `/owners/${encode(entity.id)}`, label: 'View customer' }
      }
      // scans / transfers dimensioned by owner (row-scoped): verified NO owner-scoped view
      // exists for either. (The RANKING form of these two DOES have a destination — see
      // buildRankingDeepLink below; this is only the "drill into one owner" case.)
      return null

    default:
      return null
  }
}

/**
 * No specific row — "show the whole ranking the way the answer stated it", not a
 * drill-into-one-record link. Per the working-conventions correction: a deep link must
 * reproduce sort as well as filter, and a link that can't is worse than none, so this
 * only fires where the destination's OWN sort is verified wired to the URL.
 *
 * Verified: `PeoplePage` (`/people`, Owners tab) reads `?sort=` and `?order=` directly
 * into its owners query (`PeoplePage.tsx:127-128`:
 * `sort: searchParams.get('sort') as OwnerSortField`,
 * `order: searchParams.get('order') as SortOrder`), and `OwnerSortField`
 * (`store/owners/types.ts:14-25`) carries exactly `'scans' | 'certificates' |
 * 'transfers'` — the three metrics that can legitimately be dimensioned by owner.
 *
 * Checked and found NOT wired: `CertificatesPage`, `ProductsPage` and `LeadsTab` have no
 * `sort`/`order` URL param at all (grepped `searchParams.get` across all three — no
 * match). The shared `Table` component itself reads `searchParams.get('sort')` into a
 * local `sortParams` variable (`Table.tsx:759`) that is never subsequently used —
 * dead code, not a wired param, on every list built on it. `Table`'s `onSortModelChange`
 * only updates local React state (`Table.tsx:1464-1467`) and never calls
 * `setSearchParams`. So sort-from-URL is a genuine, verified dead end everywhere except
 * PeoplePage's owners query.
 *
 * NOT reproduced: a time window. The Ask query may be date-bounded ("scans this month"),
 * but the only date-range param any destination reads is PeoplePage's
 * `registeredFrom`/`registeredTo` — the OWNER's registration date, a different axis
 * entirely from a scan/certificate/transfer event date. Wiring a scan-count time window
 * through it would silently show all-time counts on a page that looks date-filtered,
 * which is the exact failure this function exists to avoid. So a ranking link never
 * carries a time window; if the underlying query was windowed, that context is lost at
 * the deep link and the caller (AskResultView) does not claim otherwise.
 */
const buildRankingDeepLink = (result: AskResultSet): AskDeepLink | null => {
  const { metric, dimension, rows } = result
  if (dimension !== 'owner') return null
  // A "ranking" needs more than one row to mean anything; a single row has no order to
  // reproduce, and the row-scoped path (or "render nothing", per AskResultView) applies instead.
  if (rows.length < 2) return null

  if (metric === 'scans' || metric === 'certificates' || metric === 'transfers') {
    const order = result.order ?? 'desc'
    return { to: `/people?sort=${metric}&order=${order}`, label: 'Show this ranking' }
  }

  // metric === 'owners' | 'leads': no matching sort field exists — 'owners' ranked by
  // 'owner' is degenerate, and OwnerSortField has no lead-count value at all — so no
  // destination can reproduce either ranking today.
  return null
}

/**
 * Build the "go see this" link for a tool result.
 *
 * - `row` given → drill into that one entity's own records (`buildRowDeepLink`).
 * - `row` omitted → the whole-ranking link, when the destination's own sort is wired to
 *   the URL (`buildRankingDeepLink`); `null` otherwise.
 *
 * Returns `null` — and means it — whenever no verified destination exists, rather than
 * ever guessing at a URL param a destination page does not actually read.
 */
export const buildAskDeepLink = (result: AskResultSet, row?: AskResultRow): AskDeepLink | null => {
  if (row) {
    if (!row.entity) return null
    return buildRowDeepLink(result, row.entity)
  }
  return buildRankingDeepLink(result)
}
